import { Skeleton, Form, Input, Button, Select, Divider, Mentions, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { BottomButtons } from 'components/SharedComponents/BottomButtons/BottomButtons';
import gql from 'graphql-tag';
import { useAuthedMutationWithNotification } from 'utils/qlAuth';
import {
  SpectacleController_generateSpectacle,
  StoryController_BuildFlyFrames,
  StoryController_BuildStoryPlan,
  StoryController_copyStory,
  StoryController_removeStory,
} from './buttons';
import { ColoredTags_predefinedTags } from 'components/Dashboard/ColoredTags';
import { PromtMutatorHelps } from 'components/Help/PromtHelps';
import { getPromtVars } from './PromtVars/PromtVars';
import { AiStoryModel } from 'interface/models';

export const StoryMain = (props: { qStory: any; storyId: number }) => {
  const [StoryController_updateStory, updateStoryRes] = useAuthedMutationWithNotification(gql`
    mutation StoryController_updateStory($id: Int!, $config: JSON!) {
      StoryController_updateStory(id: $id, config: $config) {
        id
      }
    }
  `);

  const story: AiStoryModel = props.qStory.data?.StoryController_getStoryById || {};
  if (props.qStory.loading || !story) {
    return <Skeleton active />;
  }

  return (
    <>
      <Form
        layout="vertical"
        initialValues={story}
        onFinish={async values => {
          const newValues = { ...values };

          if (newValues.extraVars) {
            try {
              JSON.parse(newValues.extraVars);
            } catch (error) {
              message.error(`Invalid JSON in extraVars field: ${error.message}`);
              return;
            }
          }

          await StoryController_updateStory({ variables: { id: Number(props.storyId), config: newValues } });
          props.qStory.refetch();
        }}
      >
        <Form.Item label="Frame generation algorithm" name={`type`} key={`type`}>
          <Select>
            <Select.Option value="story">V1: One narrator shares a story</Select.Option>
            <Select.Option value="spectacle">V2: Spectacle</Select.Option>
            <Select.Option value="fly">V3: Based on a short description for each frame (fly)</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label="Title" name={`title`} key={`title`}>
          <Input name="title" />
        </Form.Item>
        <Form.Item label="Tags" name="tags" key="tags">
          <Select mode="multiple" allowClear style={{ width: '100%' }} placeholder="Select tags" options={ColoredTags_predefinedTags} />
        </Form.Item>
        {['spectacle', 'story'].includes(story.type) && (
          <Form.Item label="Frames count" name={`fraimsCount`} key={`fraimsCount`} help="Number of frames in the video to be generated">
            <Input name="fraimsCount" type="number" />
          </Form.Item>
        )}
        <Form.Item
          label="Init Message"
          name={`initMessage`}
          help={
            <>
              Help me generate prompts for runwayml in English based on this plot in the style of a high-tech bright human future. I am making a video
              in a super realistic style. Like in a movie.
            </>
          }
        >
          <TextArea rows={4} />
        </Form.Item>
        {story.type === 'spectacle' && (
          <i>
            Create a plot for a video about SCP 113 in the format of a spectacle with 2 characters for a 10-minute video. Each actor needs at least 10
            lines.
          </i>
        )}
        {story.type === 'story' && <i>Attach the text that will be processed into a story</i>}
        <Divider />

        <Form.Item
          label="TTS Text"
          name={`ttsText`}
          help="Text generated for the voiceover of the entire video (add '---' to split into scenes, a scene should not be longer than 30 seconds)"
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item label="Prompt vars" name={`extraVars`} help="Additional variables for prompts">
          <TextArea rows={8} />
        </Form.Item>

        {['fly', 'story'].includes(story.type) && (
          <Form.Item
            label="Prompt template header (Intro text copied to all prompts)"
            name={`promtTemplateIntro`}
            help={<>Make me a prompt in English in a super realistic style for rendering a frame for a video</>}
          >
            <Mentions rows={8} options={getPromtVars(story)} />
          </Form.Item>
        )}
        {story.type === 'fly' && (
          <Form.Item label="Prompt template (List of prompts separated by ---)" name={`promtTemplate`}>
            <Mentions rows={16} options={getPromtVars(story)} />
          </Form.Item>
        )}
        {['fly', 'story'].includes(story.type) && (
          <Form.Item label="Prompt mutator (List of prompts separated by ---)" name={`promtMutator`} help={<PromtMutatorHelps />}>
            <Mentions rows={8} options={getPromtVars(story)} />
          </Form.Item>
        )}
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <BottomButtons
          extra={
            <>
              <StoryController_copyStory qStory={props.qStory} storyId={props.storyId} />
              <StoryController_removeStory qStory={props.qStory} storyId={props.storyId} />
            </>
          }
        >
          <Button type="primary" htmlType="submit" size="small" loading={updateStoryRes.loading} disabled={updateStoryRes.loading}>
            Save
          </Button>
          {story.type === 'spectacle' && <SpectacleController_generateSpectacle qStory={props.qStory} storyId={props.storyId} />}
          {story.type === 'story' && <StoryController_BuildStoryPlan qStory={props.qStory} storyId={Number(props.storyId)} />}
          {story.type === 'fly' && <StoryController_BuildFlyFrames qStory={props.qStory} storyId={Number(props.storyId)} />}
        </BottomButtons>
      </Form>
    </>
  );
};
